import {
  GenericForm,
  GenericFormLogicalField,
  genericBeforeValidate_removeDefaultKeyedError,
  ValidationEvent,
  GenericTextFieldValidationOptions,
  FormFieldExtensionKeys,
} from "../../../hooks";
import { ACountry } from "../../../model";

import {
  DataSourceAddressAutocompleteControllerConsumerInterface,
  DataSourceAddressAutocompleteQueryConsumerInterface,
  DataSourceAddressAutocompleteQueryProviderProps,
  DataSourceAddressParseControllerConsumerInterface,
  DataSourceAddressParseQueryConsumerInterface,
} from "../../../providers";

import { AutocompleteInputDebouncedValidation } from "../../ui";

export interface AppMinimumRequirementsFor_AddressAutocomplete {
  countryCode: string;

  addressAutocomplete?: DataSourceAddressAutocompleteQueryConsumerInterface;
  addressAutocomplete_asController: DataSourceAddressAutocompleteControllerConsumerInterface;

  addressParse?: DataSourceAddressParseQueryConsumerInterface;
  addressParse_asController: DataSourceAddressParseControllerConsumerInterface;
}

interface AddressAutocompleteFormLogicProps<
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_AddressAutocomplete
> {
  required?: boolean;

  guard?: GenericTextFieldValidationOptions<Form, App>["guard"];
}

export enum FormFieldExtensionKeys_AddressAutocomplete {
  DebouncedLoadingInProgress = "DebouncedLoadingInProgress",
  ValidatingAddressSelectionInProgress = "ValidatingAddressSelectionInProgress",
  SelectedFromAutocomplete = "_selectedFromAutocomplete",
}

export enum FormFieldExtensionKeys_AddressAutocomplete_Expects {
  AutocompleteAddressLine = "_autocompleteAddress",
}

export enum FormFieldExtensionKeys_AddressAutocomplete_ManualFields {
  ManualAddressLine1 = "_manualAddressLine1",
  ManualAddressLine2 = "_manualAddressLine2",
  ManualAddressSuburb = "_manualAddressSuburb",
  ManualAddressPostcode = "_manualAddressPostcode",
  ManualAddressState = "_manualAddressState",
  ManualAddressCountry = "_manualAddressCountry",
  ManualAddressFromParsed = "_manualAddressFromParsed",
}

export const addressAutocompleteFormLogic = <
  Form extends GenericForm,
  App extends AppMinimumRequirementsFor_AddressAutocomplete
>(
  props: AddressAutocompleteFormLogicProps<Form, App> = {}
): GenericFormLogicalField<Form, App> => ({
  presentation: props,

  handleDebounceDispatch: (context) => {
    const requestSuggestions: DataSourceAddressAutocompleteQueryProviderProps =
      {
        addressToAutocomplete: context.form[context.name],
        countryCode: context.app.countryCode,
      };
    context.app.addressAutocomplete_asController.request(requestSuggestions);

    const requestAddressParse = {
      countryCode: context.app.countryCode,
      addressToParse: !!context.form[
        `${context.name}${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`
      ]
        ? context.form[`${context.name}`]
        : "",
    };
    context.app.addressParse_asController.request(requestAddressParse);
  },
  produceEffectDependencies: (context) => [
    context.app.addressAutocomplete?.data?.data,
    context.app.addressAutocomplete?.status,
    context.app.addressParse?.data?.data,
    context.app.addressParse?.status,
    context.app.addressParse?.originalRequest,
  ],
  handleEffect(context) {
    const addressAutocomplete_queryResponse =
      context.app.addressAutocomplete?.data;
    let suggestions: AutocompleteInputDebouncedValidation["suggestions"] = [];
    if (
      addressAutocomplete_queryResponse &&
      addressAutocomplete_queryResponse.data
    ) {
      suggestions = addressAutocomplete_queryResponse.data;
    }
    const autocompleteSuggestions: AutocompleteInputDebouncedValidation = {
      suggestions,
    };

    const hasAlreadyBeenParsed =
      !!context.form[
        `${context.name}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressFromParsed}`
      ];
    const isThisAddressNotEmpty = !!context.form[`${context.name}`];
    const hasThisAddressBeenSelectedFromSuggestions =
      !!context.form[
        `${context.name}${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`
      ];
    const isTheAddressThatHasBeenParsedThisAddress =
      context.app.addressParse?.originalRequest?.addressToParse ===
      context.form[`${context.name}`];
    const parsedAddress = context.app.addressParse?.data?.data;
    const hasThisAddressBeenParsed =
      !hasAlreadyBeenParsed &&
      isThisAddressNotEmpty &&
      hasThisAddressBeenSelectedFromSuggestions &&
      isTheAddressThatHasBeenParsedThisAddress &&
      !!parsedAddress;
    if (hasThisAddressBeenParsed) {
      const thisFieldName = context.name.substring(
        0,
        context.name.indexOf(
          FormFieldExtensionKeys_AddressAutocomplete_Expects.AutocompleteAddressLine
        )
      );

      const correspondingCountryEntry = Object.entries({
        AUSTRALIA: ACountry.Australia,
        "NEW ZEALAND": ACountry.NewZealand,
      }).find(([key, value]) => key === parsedAddress.country_id);
      const country = correspondingCountryEntry
        ? correspondingCountryEntry[1]
        : "";

      [
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressLine1}`,
          value: parsedAddress.street0,
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressLine2}`,
          value: parsedAddress.street1 || "",
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressSuburb}`,
          value: parsedAddress.city || "",
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressPostcode}`,
          value: parsedAddress.postcode || "",
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressState}`,
          value: parsedAddress.region?.toUpperCase() || "",
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressCountry}`,
          value: country,
        },
        {
          name: `${thisFieldName}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressFromParsed}`,
          value: true,
        },
      ].forEach((manualFormUpdate) => {
        context = context.yield.validationEvent(context, {
          name: manualFormUpdate.name,
          value: manualFormUpdate.value,
          validationEvent: ValidationEvent.onChange,
        });
      });
    }

    const partialForm = {
      [`${context.name}${FormFieldExtensionKeys.DebouncedValidation}`]:
        autocompleteSuggestions,
      [`${context.name}${FormFieldExtensionKeys_AddressAutocomplete.DebouncedLoadingInProgress}`]:
        context.app.addressAutocomplete?.status === "loading",
      [`${context.name}${FormFieldExtensionKeys_AddressAutocomplete.ValidatingAddressSelectionInProgress}`]:
        context.app.addressParse?.status === "loading",
    } as Partial<Form>;
    context.mutate.withPartialForm(context, partialForm);
  },

  validationEvents: [
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  cleanEvents: [
    ValidationEvent.onFocus,
    ValidationEvent.onChange,
    ValidationEvent.onBlur,
    ValidationEvent.onSubmit,
  ],
  beforeValidate: genericBeforeValidate_removeDefaultKeyedError,
  validate: (context) => {
    if (props.guard && !props.guard(context)) {
      return;
    }

    const anyEventWhereTheValueChanges = [ValidationEvent.onChange];
    if (anyEventWhereTheValueChanges.includes(context.validationEvent)) {
      const partialForm = {
        [`${context.name}${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`]:
          false,
        [`${context.name}${FormFieldExtensionKeys_AddressAutocomplete_ManualFields.ManualAddressFromParsed}`]:
          false,
      } as Partial<Form>;
      context = context.mutate.withPartialForm(context, partialForm);
    }

    const anyEventWhereTheFieldNeedsToBeValidatedImmediately = [
      ValidationEvent.onBlur,
      ValidationEvent.onSubmit,
    ];
    if (
      anyEventWhereTheFieldNeedsToBeValidatedImmediately.includes(
        context.validationEvent
      )
    ) {
      const haveIBeenSelectedFromTheAutocompleteSuggestions =
        context.form[
          `${context.name}${FormFieldExtensionKeys_AddressAutocomplete.SelectedFromAutocomplete}`
        ];
      if (!haveIBeenSelectedFromTheAutocompleteSuggestions && props.required) {
        const partialForm = {
          [context.name]: "",
          [`${context.name}Error`]: "This field is required.",
        } as Partial<Form>;
        context = context.mutate.withPartialForm(context, partialForm);
      }
    }
  },
});
